import { Box, Center, Flex, Spinner } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { atom, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import ButtonedRadioGroup from '../../../components/ButtonedRadioGroup';
import { useLEAccordionItemContext } from '../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import TextWithTooltip from '../../../components/TextWithTooltip';
import { Label } from '../../../components/formComponents';
import DateInput from '../../../components/formComponents/DateInput';
import LoanAppSSNInput from '../../../components/formComponents/ssn/LoanAppSsnInput';
import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import { getTempInfoCountryConfig } from '../../../constants/countries';
import { options } from '../../../constants/maritalStatuses';
import {
  StateAbbreviation,
  useGetMethodFiSessionStateQuery,
  useTemporaryInfoSsnUpdateMutation,
  useTemporaryInfoUpdateMutation,
} from '../../../gql/generated/graphql';
import { usePrequalification, useStep, useTempInfo } from '../../../hooks';
import { trackFlowFinish } from '../../../services/analytics/conversionEvents';
import { identify } from '../../../services/analytics/rudderstack';
import { formatDateToString } from '../../../utils/dates';
import { hasCobuyer } from '../../../utils/tempInfo';
import {
  SsnAndBirthdayValues,
  ssnAndBirthdayInitialValues,
  ssnAndBirthdayValidationSchema,
} from '../utils';
import AddCobuyerButton from './AddCobuyerButton';

export const prequalRanInMktAtom = atom<boolean>(false);

const SsnAndBirthdate = () => {
  const step = useStep();

  const setPrequalRanInMktAtom = useSetAtom(prequalRanInMktAtom);

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const [temporaryInfoSsnUpdate] = useTemporaryInfoSsnUpdateMutation();

  const { updateState, isExpanded } = useLEAccordionItemContext();
  const { info } = useTempInfo({ fetchPolicy: 'network-only', refresh: isExpanded });
  const tempInfoData = info?.data;
  const { creditPull } = getTempInfoCountryConfig(tempInfoData);
  const methodFiEnabled = !!tempInfoData?.is_methodfi_flow;
  const prequalRanInMkt = !!tempInfoData?.prequal_id;

  const { data: methodFiData, loading: methodFiLoading } = useGetMethodFiSessionStateQuery({
    fetchPolicy: 'network-only',
    skip: !methodFiEnabled || !isExpanded,
    context: {
      isErrorHandled: true,
    },
  });

  const methodFiTempInfoData = methodFiData?.getMethodFiSessionState?.tempInfoFormattedData;

  const {
    shouldExecutePrequalification,
    isSubmitStep,
    consentLoading,
    termsLoading,
    executePrequalification,
    savePrivacyConsents,
    ConsentContent,
    TermsAndConditionsModal,
  } = usePrequalification({ skip: !!tempInfoData?.prequal_id, state: tempInfoData?.state });

  useEffect(() => {
    setPrequalRanInMktAtom(prequalRanInMkt);
  }, [prequalRanInMkt]);

  const onSubmitForm = async (values: SsnAndBirthdayValues, addCobuyer: boolean) => {
    updateState({ status: 'loading' });

    if (values.must_enter_ssn) {
      await temporaryInfoSsnUpdate({
        variables: {
          tempInfoId: info?.id ?? '',
          ssn: values.ssn,
          isCobuyer: false,
        },
      });
    }

    const { data: updatedInfo } = await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data: {
            dob: values.dob,
            marital_status: values.marital_status,
            include_cobuyer: addCobuyer,
          },
        },
      },
    });

    identify({
      birthday: values.dob != null ? formatDateToString(values.dob, 'yyyy-MM-dd') : undefined,
    });

    if (!(await savePrivacyConsents())) {
      updateState({ status: 'error' });
      return;
    }

    if (!addCobuyer) {
      await executePrequalification(updatedInfo?.temporaryInfoUpdate?.data);
    }

    updateState({ status: 'complete' });

    if (!addCobuyer) {
      trackFlowFinish(info?.id);
    }

    step.moveNext({ hasCobuyer: addCobuyer });
  };

  const fromWisconsin = tempInfoData?.state === StateAbbreviation.Wi;
  const itHasCobuyer = hasCobuyer(tempInfoData);

  if (!tempInfoData) {
    return null;
  }

  const isLoadingPrefillData = methodFiEnabled && methodFiLoading;
  const submitLabel = isSubmitStep ? 'SUBMIT' : 'NEXT';

  if (isLoadingPrefillData) {
    return (
      <Center my={8}>
        <Spinner size="xl" color="leaseEndBlue" />
      </Center>
    );
  }

  return (
    <Box>
      <Center mb={{ base: 4, md: 0 }}>
        <TextWithTooltip label="Providing this information helps us fulfill lender requirements to verify your identity.">
          Why do we need this?
        </TextWithTooltip>
      </Center>
      <Formik
        enableReinitialize
        initialValues={ssnAndBirthdayInitialValues(
          tempInfoData,
          methodFiEnabled ? methodFiTempInfoData : null,
        )}
        validationSchema={ssnAndBirthdayValidationSchema(fromWisconsin)}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={(values) => onSubmitForm(values, itHasCobuyer || false)}
      >
        {({ isSubmitting, values, setFieldValue, validateForm, setErrors, setFieldTouched }) => (
          <Form>
            <Flex
              flexDir={{ base: 'column', md: 'row' }}
              alignItems="flex-start"
              mt={4}
              mx="auto"
              w={{ base: '100%', md: '90%', lg: '95%', xl: '100%' }}
            >
              <DateInput
                _container={{ mr: { base: 0, md: 2 }, mb: { base: 4, md: 0 } }}
                hideCalendar
                label="BIRTHDATE"
                name="dob"
              />
              {creditPull && (
                <LoanAppSSNInput tempInfoData={tempInfoData} tempInfoId={info?.id ?? undefined} />
              )}
            </Flex>
            {fromWisconsin && (
              <Box mt={4} mb={10}>
                <Label mb={2}>MARITAL STATUS</Label>
                <ButtonedRadioGroup
                  columns={{ base: 2, md: 4 }}
                  value={values.marital_status}
                  onChange={(value: string) => setFieldValue('marital_status', value)}
                  options={options}
                  name="marital_status"
                />
              </Box>
            )}

            <Flex flexDir="column" alignItems="center" gap={6} mt="30px">
              {ConsentContent}
              <PrimaryButton
                trackClick={
                  shouldExecutePrequalification && !itHasCobuyer
                    ? 'ssn_and_birthday_consent_and_submit'
                    : undefined
                }
                loading={isSubmitting}
                isDisabled={isSubmitting || consentLoading || termsLoading}
                type="submit"
              >
                {itHasCobuyer ? 'EDIT CO-BUYER INFO' : submitLabel}
              </PrimaryButton>
              <AddCobuyerButton
                isDisabled={isSubmitting || consentLoading || termsLoading}
                onClick={async () => {
                  setFieldTouched('dob', true);
                  setFieldTouched('ssn', true);
                  const errors = await validateForm(values);
                  if (Object.keys(errors).length > 0) {
                    setErrors(errors);
                    return;
                  }

                  onSubmitForm(values, true);
                }}
              />
            </Flex>
          </Form>
        )}
      </Formik>

      {TermsAndConditionsModal}
    </Box>
  );
};

export default SsnAndBirthdate;
